import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Package, MapPin, Calendar, DollarSign, FileText } from 'lucide-react';
import { useThemeColors } from '../theme/ThemeContext';
import Select from 'react-select';
import { Country, State, City } from "country-state-city";
import { feesAPI } from '../services/api';
import toast from 'react-hot-toast';

// Get countries from package
const countries = Country.getAllCountries();

// Get cities for selected country
const getCountryCities = (countryCode) => {
  if (!countryCode) return [];
  
  try {
    console.log(`Getting cities for country: ${countryCode}`);
    
    // Get all states for country
    const states = State.getStatesOfCountry(countryCode);
    console.log(`States for ${countryCode}:`, states);
    
    if (!states || states.length === 0) {
      console.log(`No states found for ${countryCode}`);
      return [];
    }
    
    // Get cities from all states
    let allCities = [];
    states.forEach(state => {
      try {
        const cities = City.getCitiesOfState(countryCode, state.isoCode);
        if (cities && cities.length > 0) {
          allCities = allCities.concat(cities);
        }
      } catch (error) {
        console.log(`Error getting cities for state ${state.isoCode}:`, error);
      }
    });
    
    // Extract city names and remove duplicates
    const cityNames = allCities.map(city => city.name);
    const uniqueCityNames = [...new Set(cityNames)];
    console.log(`Final cities for ${countryCode}:`, uniqueCityNames.slice(0, 20)); // Show first 20
    return uniqueCityNames.sort();
  } catch (error) {
    console.log(`Error getting cities for country ${countryCode}:`, error);
    return [];
  }
};

const EditParcelForm = ({ parcel, onSubmit, onCancel, isLoading }) => {
  console.log(parcel);
  const colors = useThemeColors();
  const { register, handleSubmit, setValue, watch, control, formState: { errors } } = useForm();
  
  const [selectedPickupCountry, setSelectedPickupCountry] = useState('');
  const [selectedDropoffCountry, setSelectedDropoffCountry] = useState('');
  const [pickupCities, setPickupCities] = useState([]);
  const [dropoffCities, setDropoffCities] = useState([]);
  const [feeEstimate, setFeeEstimate] = useState(null);
  const [isCalculatingFee, setIsCalculatingFee] = useState(false);

  const watchedValues = watch();
  
  const onFormSubmit = (data) => {
    const parcelData = {
      id: parcel.id,
      pickup_city: data.pickup_city,
      pickup_country: data.pickup_country,
      dropoff_city: data.dropoff_city,
      dropoff_country: data.dropoff_country,
      weight_kg: parseFloat(data.weight_kg),
      category: data.category,
      notes: data.notes,
      expires_at: data.expires_at,
      // Handle checkbox logic
      guaranteed_delivery: data.guaranteedDelivery || false,
      purchased: data.purchased || false,
      declared_value: data.guaranteedDelivery ? parseFloat(data.declared_value) || 0 : null,
      purchase_price: data.purchased ? parseFloat(data.purchase_price) : null,
      max_delivery_price: parseFloat(data.max_delivery_price) || 0,
      receiver_name: data.receiver_name || '',
      receiver_phone: data.receiver_phone || ''
    };
    onSubmit(parcelData);
  };
  
  // Calculate AI suggested price based on parcel details
  const calculateSuggestedPrice = () => {
    if (!watchedValues.weight_kg || (!watchedValues.declared_value && !watchedValues.purchase_price)) {
      return 0;
    }

    const weight = parseFloat(watchedValues.weight_kg);
    const value = parseFloat(watchedValues.declared_value) || parseFloat(watchedValues.purchase_price) || 0;
    
    // Base pricing calculation
    let basePrice = 100; // Base delivery fee
    
    // Weight-based pricing
    if (weight <= 1) basePrice += 50;
    else if (weight <= 5) basePrice += 100;
    else if (weight <= 10) basePrice += 200;
    else basePrice += 350;
    
    // Value-based multiplier
    if (value > 0) {
      basePrice += value * 0.05; // 5% of declared value
    }
    
    // Distance simulation (simplified - would use real distance API)
    const distanceMultiplier = 1.2; // Assume medium distance
    
    // Market demand factor (simplified)
    const demandFactor = 1.1; // Slight demand premium
    
    return Math.round(basePrice * distanceMultiplier * demandFactor);
  };

  // Calculate fee estimate when relevant fields change
  useEffect(() => {
    const calculateFee = async () => {
      if (watchedValues.weight_kg || watchedValues.declared_value || watchedValues.purchase_price) {
        setIsCalculatingFee(true);
        try {
          const response = await feesAPI.calculateFeeEstimate('update_parcel', {
            weight_kg: parseFloat(watchedValues.weight_kg),
            declared_value: parseFloat(watchedValues.declared_value) || 0,
            purchase_price: parseFloat(watchedValues.purchase_price) || 0,
            guaranteed_delivery: watchedValues.guaranteedDelivery || false,
            purchased: watchedValues.purchased || false,
            max_delivery_price: parseFloat(watchedValues.max_delivery_price) || 0,
            id: parcel.id
          });
          setFeeEstimate(response.data.data);
        } catch (error) {
          console.error('Failed to calculate fee:', error);
        } finally {
          setIsCalculatingFee(false);
        }
      }
    };

    const timeoutId = setTimeout(calculateFee, 500);
    return () => clearTimeout(timeoutId);
  }, [watchedValues.weight_kg, watchedValues.declared_value, watchedValues.purchase_price, watchedValues.max_delivery_price, watchedValues.guaranteedDelivery, watchedValues.purchased]);

  // Handlers for country selection
  const handlePickupCountryChange = (country) => {
    setSelectedPickupCountry(country);
    setValue('pickup_country', country);
    // Clear city when country changes
    setValue('pickup_city', '');
    // Load cities for selected country
    if (country) {
      const countryCode = countries.find(c => c.name === country)?.isoCode;
      if (countryCode) {
        const cities = getCountryCities(countryCode);
        setPickupCities(cities);
      }
    } else {
      setPickupCities([]);
    }
  };

  const handleDropoffCountryChange = (country) => {
    setSelectedDropoffCountry(country);
    setValue('dropoff_country', country);
    // Clear city when country changes
    setValue('dropoff_city', '');
    // Load cities for selected country
    if (country) {
      const countryCode = countries.find(c => c.name === country)?.isoCode;
      if (countryCode) {
        const cities = getCountryCities(countryCode);
        setDropoffCities(cities);
      }
    } else {
      setDropoffCities([]);
    }
  };

  useEffect(() => {
    if (parcel) {
      console.log(parcel);
      
      setValue('category', parcel.category);
      setValue('weight_kg', parcel.weight_kg);
      setValue('max_delivery_price', parcel.max_delivery_price || '');
      setValue('pickup_country', parcel.pickup_country);
      setValue('dropoff_country', parcel.dropoff_country);
      setValue('expires_at', parcel.expires_at ? parcel.expires_at.split('T')[0] : '');
      setValue('notes', parcel.notes || '');
      setValue('receiver_name', parcel.receiver_name || '');
      setValue('receiver_phone', parcel.receiver_phone || '');
      
      // Set selected countries for display
      setSelectedPickupCountry(parcel.pickup_country || '');
      setSelectedDropoffCountry(parcel.dropoff_country || '');
      
      // Load cities for existing countries and set selected cities
      if (parcel.pickup_country) {
        const pickupCountryCode = countries.find(c => c.name === parcel.pickup_country)?.isoCode;
        if (pickupCountryCode) {
          const cities = getCountryCities(pickupCountryCode);
          setPickupCities(cities);
          // Check if the current pickup city exists in the loaded cities
          if (parcel.pickup && cities.includes(parcel.pickup)) {
            // City will be automatically selected by the Controller since we set the form value above
            // console.log('Pickup city found in loaded cities:', parcel.pickup);
            setValue('pickup_city', parcel.pickup);
          } else {
            toast.success('Pickup city not found in loaded cities, will use manual input:', parcel.pickup);
          }
        }
      }
      
      if (parcel.dropoff_country) {
        const dropoffCountryCode = countries.find(c => c.name === parcel.dropoff_country)?.isoCode;
        if (dropoffCountryCode) {
          const cities = getCountryCities(dropoffCountryCode);
          setDropoffCities(cities);
          // Check if the current dropoff city exists in the loaded cities
          if (parcel.dropoff && cities.includes(parcel.dropoff)) {
            // City will be automatically selected by the Controller since we set the form value above
            // console.log('Dropoff city found in loaded cities:', parcel.dropoff);
            setValue('dropoff_city', parcel.dropoff);
          } else {
            toast.success('Dropoff city not found in loaded cities, will use manual input:', parcel.dropoff);
          }
        }
      }
      
      // Set delivery options based on parcel data
      setValue('guaranteedDelivery', parcel.guaranteed_delivery || false);
      setValue('purchased', parcel.purchased || false);
      if (parcel.declared_value) {
        setValue('declared_value', parcel.declared_value);
      }
      if (parcel.purchase_price) {
        setValue('purchase_price', parcel.purchase_price);
      }
    }
  }, [parcel, setValue]);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-theme-primary mb-1">
          <Package className="inline h-4 w-4 mr-1" />
          Category
        </label>
        <select
          {...register('category', { required: 'Category is required' })}
          className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
        >
          <option value="">Select a category</option>
          <option value="documents">Documents</option>
          <option value="electronics">Electronics</option>
          <option value="clothing">Clothing</option>
          <option value="food">Food</option>
          <option value="other">Other</option>
        </select>
        {errors.category && (
          <p className="mt-1 text-sm text-theme-error">{errors.category.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-theme-primary mb-1">
          <Package className="inline h-4 w-4 mr-1" />
          Weight (kg)
        </label>
        <input
          type="number"
          step="0.1"
          {...register('weight_kg', { 
            required: 'Weight is required',
            min: { value: 0.1, message: 'Weight must be at least 0.1kg' }
          })}
          className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
          placeholder="2.5"
        />
        {errors.weight_kg && (
          <p className="mt-1 text-sm text-theme-error">{errors.weight_kg.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">
            <MapPin className="inline h-4 w-4 mr-1" />
            Pickup City
          </label>
          {selectedPickupCountry && pickupCities.length > 0 ? (
            <Controller
              name="pickup_city"
              control={control}
              rules={{ required: 'Pickup city is required' }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select
                    {...field}
                    options={pickupCities.map(city => ({
                      value: city,
                      label: city
                    }))}
                    placeholder="Select city"
                    className="w-full"
                    classNamePrefix="react-select"
                    value={field.value ? { value: field.value, label: field.value } : null}
                    onChange={(selectedOption) => field.onChange(selectedOption?.value || '')}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                        '&:hover': { borderColor: colors.primary[400] },
                        boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 0.75rem'
                      })
                    }}
                  />
                  {error && (
                    <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                  )}
                </>
              )}
            />
          ) : (
            <input
              {...register('pickup_city', { required: 'Pickup city is required' })}
              type="text"
              className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
              placeholder="e.g., Addis Ababa"
            />
          )}
          {errors.pickup_city && !selectedPickupCountry && (
            <p className="mt-1 text-sm text-theme-error">{errors.pickup_city.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">
            <MapPin className="inline h-4 w-4 mr-1" />
            Pickup Country *
          </label>
          <Controller
            name="pickup_country"
            control={control}
            rules={{ required: 'Pickup country is required' }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  {...field}
                  options={countries.map(country => ({
                    value: country.name,
                    label: country.name,
                    isoCode: country.isoCode
                  }))}
                  placeholder="Select country"
                  className="w-full"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    const value = selectedOption?.value || '';
                    field.onChange(value);
                    setSelectedPickupCountry(value);
                    setValue('pickup_city', '');
                    // Load cities for selected country
                    if (value) {
                      const countryCode = countries.find(c => c.name === value)?.isoCode;
                      if (countryCode) {
                        const cities = getCountryCities(countryCode);
                        setPickupCities(cities);
                      }
                    } else {
                      setPickupCities([]);
                    }
                  }}
                  value={field.value ? { value: field.value, label: field.value } : null}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                      '&:hover': { borderColor: colors.primary[400] },
                      boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                      borderRadius: '0.375rem',
                      padding: '0.5rem 0.75rem'
                    })
                  }}
                />
                {error && (
                  <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                )}
              </>
            )}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">
            <MapPin className="inline h-4 w-4 mr-1" />
            Dropoff City
          </label>
          {selectedDropoffCountry && dropoffCities.length > 0 ? (
            <Controller
              name="dropoff_city"
              control={control}
              rules={{ required: 'Dropoff city is required' }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select
                    {...field}
                    options={dropoffCities.map(city => ({
                      value: city,
                      label: city
                    }))}
                    placeholder="Select city"
                    className="w-full"
                    classNamePrefix="react-select"
                    value={field.value ? { value: field.value, label: field.value } : null}
                    onChange={(selectedOption) => field.onChange(selectedOption?.value || '')}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                        '&:hover': { borderColor: colors.primary[400] },
                        boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 0.75rem'
                      })
                    }}
                  />
                  {error && (
                    <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                  )}
                </>
              )}
            />
          ) : (
            <input
              {...register('dropoff_city', { required: 'Dropoff city is required' })}
              type="text"
              className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
              placeholder="e.g., Dire Dawa"
            />
          )}
          {errors.dropoff_city && !selectedDropoffCountry && (
            <p className="mt-1 text-sm text-theme-error">{errors.dropoff_city.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">
            <MapPin className="inline h-4 w-4 mr-1" />
            Dropoff Country *
          </label>
          <Controller
            name="dropoff_country"
            control={control}
            rules={{ required: 'Dropoff country is required' }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  {...field}
                  options={countries.map(country => ({
                    value: country.name,
                    label: country.name,
                    isoCode: country.isoCode
                  }))}
                  placeholder="Select country"
                  className="w-full"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    const value = selectedOption?.value || '';
                    field.onChange(value);
                    setSelectedDropoffCountry(value);
                    setValue('dropoff_city', '');
                    // Load cities for selected country
                    if (value) {
                      const countryCode = countries.find(c => c.name === value)?.isoCode;
                      if (countryCode) {
                        const cities = getCountryCities(countryCode);
                        setDropoffCities(cities);
                      }
                    } else {
                      setDropoffCities([]);
                    }
                  }}
                  value={field.value ? { value: field.value, label: field.value } : null}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                      '&:hover': { borderColor: colors.primary[400] },
                      boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                      borderRadius: '0.375rem',
                      padding: '0.5rem 0.75rem'
                    })
                  }}
                />
                {error && (
                  <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                )}
              </>
            )}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-theme-primary mb-1">
          <Calendar className="inline h-4 w-4 mr-1" />
          Expiry Date
        </label>
        <input
          type="date"
          {...register('expires_at', { required: 'Expiry date is required' })}
          className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
        />
        {errors.expires_at && (
          <p className="mt-1 text-sm text-theme-error">{errors.expires_at.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">
            Receiver Name
          </label>
          <input
            type="text"
            {...register('receiver_name')}
            className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
            placeholder="Receiver name"
          />
          {errors.receiver_name && (
            <p className="mt-1 text-sm text-theme-error">{errors.receiver_name.message}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-theme-primary mb-1">
          Receiver Phone
        </label>
        <input
          type="tel"
          {...register('receiver_phone')}
          className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
          placeholder="Receiver phone (+251...)"
        />
        {errors.receiver_phone && (
          <p className="mt-1 text-sm text-theme-error">{errors.receiver_phone.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
          <FileText className="inline h-4 w-4 mr-1" />
          Notes
        </label>
        <textarea
          {...register('notes')}
          rows={3}
          className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
          placeholder="Describe your parcel contents and any special requirements..."
        />
        {errors.notes && (
          <p className="mt-1 text-sm text-theme-error">{errors.notes.message}</p>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-theme-primary" />
          Delivery Options
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-3">
              Additional Services
            </label>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  {...register('guaranteedDelivery')}
                  type="checkbox"
                  className="h-4 w-4 text-theme-primary focus:ring-blue-500 border-theme-light"
                />
                <label className="ml-2 text-sm text-theme-secondary">
                  Guaranteed Delivery
                  <span className="text-xs text-theme-muted ml-1 block">Faster delivery with time guarantee</span>
                </label>
              </div>
              <div className="flex items-center">
                <input
                  {...register('purchased')}
                  type="checkbox"
                  className="h-4 w-4 text-theme-primary focus:ring-blue-500 border-theme-light"
                />
                <label className="ml-2 text-sm text-theme-secondary">
                  Purchased Item
                  <span className="text-xs text-theme-muted ml-1 block">Item purchased by traveler</span>
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
              <DollarSign className="inline h-4 w-4 mr-1" />
              Max Delivery Price (ETB)
              <span className="text-xs text-theme-muted ml-1">(Maximum amount you'll pay for delivery)</span>
            </label>
            <input
              type="number"
              step="0.01"
              {...register('max_delivery_price', { 
                required: 'Max delivery price is required',
                min: { value: 0.01, message: 'Price must be greater than 0' }
              })}
              className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
              placeholder="e.g., 500.00"
            />
            {errors.max_delivery_price && (
              <p className="mt-1 text-sm text-theme-error">{errors.max_delivery_price.message}</p>
            )}
          </div>

          {watch('guaranteedDelivery') && (
            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Declared Value (ETB)
                <span className="text-xs text-theme-muted ml-1">(Value of items being delivered)</span>
              </label>
              <input
                {...register('declared_value', { 
                  min: { value: 0, message: 'Value must be positive' }
                })}
                type="number"
                step="0.01"
                className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
                placeholder="0.00"
              />
              {errors.declared_value && (
                <p className="mt-1 text-sm text-theme-error">{errors.declared_value.message}</p>
              )}
            </div>
          )}

          {watch('purchased') && (
            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Purchase Price (ETB)
                <span className="text-xs text-theme-muted ml-1">(Cost of purchased items)</span>
              </label>
              <input
                {...register('purchase_price', {
                  min: { value: 0.01, message: 'Price must be greater than 0' }
                })}
                type="number"
                step="0.01"
                className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
                placeholder="e.g., 500.00"
              />
              {errors.purchase_price && (
                <p className="mt-1 text-sm text-theme-error">{errors.purchase_price.message}</p>
              )}
            </div>
          )}

          {!watch('guaranteedDelivery') && !watch('purchased') && (
            <div className="text-sm text-theme-muted p-4 bg-theme-neutral-50 rounded-md border border-theme-neutral-200">
              <p>Standard delivery selected - No additional information needed</p>
            </div>
          )}
        </div>
      </div>

      {/* Fee Estimation */}
      {feeEstimate && (
        <div className="bg-theme-neutral-50 border border-theme-neutral-200 rounded-md p-4 mb-4">
          <div className="flex items-start">
                <DollarSign className="h-5 w-5 text-theme-primary mt-0.5 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-theme-primary text-theme-primary">Fee Estimate</h4>
                  <div className="mt-1 text-sm text-theme-secondary">
                    <div>Update Fee: {feeEstimate.posting_fee} ETB</div>
                    {/* {feeEstimate.deposit_requirement > 0 && ( */}
                    <div>Security Deposit: {feeEstimate.deposit_requirement} ETB</div>
                    <div>Delivery Fee: {feeEstimate.delivery_fee} ETB</div>
                    {/* )} */}
                    <div className="font-medium text-theme-primary mt-1">
                      Total: {feeEstimate.total_cost} ETB
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-theme-secondary bg-theme-neutral-100 rounded-md hover:bg-theme-neutral-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-theme-primary text-theme-inverse rounded-md hover:bg-theme-primary-hover disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default EditParcelForm;
